import { useMemo } from 'react';
import { useI18n } from './index';
import { projects } from '../data/projects';
import { workflows } from '../data/workflows';
import { skillCategories } from '../data/skills';
import { THEMES } from '../data/themes';
import { architectures } from '../data/architectures';

/**
 * The site's content in the reader's language.
 *
 * English lives in the data files and is the base. A locale file carries only
 * the strings that differ, keyed by the `id` every record already has, and is
 * laid over the base one field at a time. A field the translator has not
 * reached falls through to English rather than blanking, which is what makes a
 * half-finished locale safe to ship.
 */

const present = (value) => typeof value === 'string' && value.trim() !== '';

/** Copy `keys` from `overlay` onto `base`, skipping anything not translated. */
function overlayFields(base, overlay, keys) {
  if (!overlay) return base;

  const changed = {};
  keys.forEach((key) => {
    const value = overlay[key];
    if (Array.isArray(value)) {
      if (value.length > 0) changed[key] = value;
    } else if (present(value)) {
      changed[key] = value;
    }
  });

  return Object.keys(changed).length === 0 ? base : { ...base, ...changed };
}

const PROJECT_FIELDS = ['title', 'type', 'description', 'benefits'];
const WORKFLOW_FIELDS = ['name', 'type', 'cadence', 'problem', 'notes'];

function mergeWorkflow(base, overlay) {
  if (!overlay) return base;

  const merged = overlayFields(base, overlay, WORKFLOW_FIELDS);
  const next = merged === base ? { ...base } : merged;

  // Stages and facts are ordered lists of pairs. Index alignment between base
  // and overlay needs no length guarantee to be safe: each half of a pair
  // falls back to English independently (via overlayFields), so a shorter or
  // absent overlay array just leaves the remaining pairs in English rather
  // than misaligning or throwing. A later task adds a length-parity test so a
  // truncated locale doesn't ship silently — it isn't required here.
  if (Array.isArray(overlay.stages)) {
    next.stages = base.stages.map((stage, i) => overlayFields(stage, overlay.stages[i], ['label', 'detail']));
  }
  if (Array.isArray(overlay.facts)) {
    next.facts = base.facts.map((fact, i) => overlayFields(fact, overlay.facts[i], ['key', 'value']));
  }

  return next;
}

function mergeArchitecture(base, overlay) {
  if (!overlay) return base;

  const layers = base.layers.map((layer, i) => {
    const label = overlay.layers?.[i];
    const nodes = layer.nodes.map((node) =>
      present(overlay.nodes?.[node.id]) ? { ...node, label: overlay.nodes[node.id] } : node
    );
    return { ...layer, label: present(label) ? label : layer.label, nodes };
  });

  const edges = base.edges.map((edge) => {
    const [from, to, label] = edge;
    if (label === undefined) return edge;
    const translated = overlay.edges?.[`${from}>${to}`];
    return present(translated) ? [from, to, translated] : edge;
  });

  return { ...base, layers, edges };
}

export function mergeContent(dict) {
  if (!dict) {
    return { projects, workflows, skillCategories, themes: THEMES, architectures };
  }

  return {
    projects: projects.map((project) =>
      overlayFields(project, dict.projects?.[project.id], PROJECT_FIELDS)
    ),

    workflows: workflows.map((workflow) => mergeWorkflow(workflow, dict.workflows?.[workflow.id])),

    skillCategories: skillCategories.map((group) =>
      present(dict.skills?.[group.id]) ? { ...group, name: dict.skills[group.id] } : group
    ),

    themes: THEMES.map((theme) => overlayFields(theme, dict.themes?.[theme.id], ['label', 'hint'])),

    architectures: Object.fromEntries(
      Object.entries(architectures).map(([id, architecture]) => [
        id,
        mergeArchitecture(architecture, dict.architectures?.[id]),
      ])
    ),
  };
}

export function useContent() {
  const { dict } = useI18n();
  return useMemo(() => mergeContent(dict), [dict]);
}
